import React, { useCallback, useEffect, useState } from 'react';
import Topbar from '@/components/layout/Topbar';
import { api } from '@/lib/api';
import { ShieldCheck, ShieldAlert, RefreshCw, Undo2 } from 'lucide-react';

const fmt = (n, d = 2) => (n === null || n === undefined || isNaN(n) ? '—' : Number(n).toFixed(d));

export default function Estado() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/status');
      setS(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetKill = async () => {
    await api.post('/system/kill-switch/reset');
    load();
  };

  return (
    <>
      <Topbar
        title="Estado del sistema"
        subtitle="Reglas R1–R14 en tiempo real · kill switches · ventana operativa"
        right={
          <button onClick={load} className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs border border-border hover:bg-muted" data-testid="btn-refresh-status">
            <RefreshCw size={14} /> Refrescar
          </button>
        }
      />
      <div className="px-3 sm:px-4 lg:px-6 py-4 space-y-4">
        {loading || !s ? (
          <div className="panel p-4 text-sm text-muted-foreground">Cargando...</div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="panel p-4">
                <div className="text-xs text-muted-foreground">Equity simulado</div>
                <div className="font-mono-num text-2xl">{fmt(s.equity_usdt)} USDT</div>
              </div>
              <div className="panel p-4">
                <div className="text-xs text-muted-foreground">PnL día</div>
                <div className={`font-mono-num text-2xl ${s.pnl_dia_usdt > 0 ? 'pnl-pos' : s.pnl_dia_usdt < 0 ? 'pnl-neg' : 'pnl-neutral'}`}>{fmt(s.pnl_dia_usdt)}</div>
              </div>
              <div className="panel p-4">
                <div className="text-xs text-muted-foreground">Posiciones abiertas</div>
                <div className="font-mono-num text-2xl">{s.posiciones_abiertas}</div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="panel p-4">
                <div className="text-xs text-muted-foreground mb-1">Ventana operativa</div>
                <div className={s.ventana_activa ? 'pnl-pos text-lg' : 'pnl-neg text-lg'}>{s.ventana_activa ? 'activa' : 'cerrada'}</div>
                <div className="text-[11px] text-muted-foreground mt-1">08:00–14:00 ET · L–V (corte entradas 12:00 ET)</div>
              </div>
              <div className="panel p-4">
                <div className="text-xs text-muted-foreground mb-1">Día verde</div>
                <div className={s.dia_verde ? 'pnl-pos text-lg' : 'pnl-neutral text-lg'}>{s.dia_verde ? 'sí' : 'no'}</div>
                <div className="text-[11px] text-muted-foreground mt-1">Reciclaje R10: sólo si día verde</div>
              </div>
              <div className="panel p-4 flex items-center justify-between">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Kill switch día</div>
                  <div className={s.kill_switch_activo ? 'pnl-neg text-lg' : 'pnl-neutral text-lg'}>{s.kill_switch_activo ? 'ACTIVO' : 'off'}</div>
                </div>
                {s.kill_switch_activo && (
                  <button onClick={resetKill} className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs border border-border hover:bg-muted" data-testid="btn-reset-kill">
                    <Undo2 size={14} /> Reactivar
                  </button>
                )}
              </div>
            </div>
            <div className="panel p-4">
              <div className="text-sm mb-3">Reglas R1–R14</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {s.reglas.map((r) => (
                  <div key={r.codigo} className={`panel-soft p-3 flex items-start gap-2 ${r.estado === 'violada' ? 'border-rose-500/40' : ''}`} data-testid={`rule-${r.codigo}`}>
                    {r.estado === 'ok'
                      ? <ShieldCheck className="text-emerald-500 mt-0.5" size={16} />
                      : <ShieldAlert className="text-rose-500 mt-0.5" size={16} />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm flex items-center gap-2"><span className="font-mono-num">{r.codigo}</span><span className={r.estado === 'ok' ? 'text-emerald-500 text-xs' : 'text-rose-500 text-xs'}>{r.estado}</span></div>
                      <div className="text-xs text-muted-foreground">{r.descripcion}</div>
                      {r.detalle && <div className="text-[11px] mt-1">{r.detalle}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
